import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import CustomRichTextEditor from "../../../components/TextEditor/CustomRichTextEditor";
import { StoragePathType, useUpdateFacultyProfileMutation } from "../../../graphql/generated/graphql";
import { useAuth } from "../../../hooks/useAuth";

const profileSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  meetingLink: z.string().url("Invalid URL").optional().or(z.literal("")),
  biography: z.string().optional(),
  profilePicture: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const FacultyProfileTabContainer = () => {
  const { meData, refetchMe } = useAuth();
  const [updateFacultyProfile] = useUpdateFacultyProfileMutation();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [activelyEditing, setActivelyEditing] = useState(false);

  const { showToast } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
      email: "",
      meetingLink: "",
      biography: "",
      profilePicture: "",
    },
  });

  useEffect(() => {
    if (meData?.me) {
      reset({
        name: meData.me.name || "",
        email: meData.me.email || "",
        meetingLink: meData.me.meetingLink || "",
        biography: meData.me.biography || "",
        profilePicture: meData.me.profilePicture || "",
      });
    }
  }, [meData?.me, reset]);

  const handleSave = handleSubmit(async (formData) => {
    setActivelyEditing(false);
    try {
      await updateFacultyProfile({
        variables: {
          input: {
            meetingLink: formData.meetingLink || "",
            biography: formData.biography || "",
            profilePicture: formData.profilePicture || "",
          },
        },
      });

      refetchMe();
      showToast({
        message: "Profile updated successfully",
        type: "success",
      });
      reset(formData);
    } catch (error) {
      console.error("Error saving profile:", error);
      showToast({
        message: "Failed to save profile changes. Please try again.",
        type: "error",
      });
    }
  });
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <CustomText text="Your Profile" size="l" weight="ultraBold" style={styles.title} />
        <CustomButton
          text="Save"
          styleType="primary"
          size="m"
          onPress={handleSave}
          leftIcon={null}
          rightIcon={null}
          style={{
            backgroundColor: !isDirty ? "#E5E7EB" : "#3E68FE",
            opacity: !isDirty ? 0.5 : 1,
          }}
          textStyle={{
            color: !isDirty ? "#6B7280" : "#FFFFFF",
          }}
          disabled={!isDirty}
          textProps={{}}
          useTranslationText={true}
        />
      </View>

      <CustomSpacing type="vertical" size="xl" />

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 40,
          padding: 40,
          borderWidth: 1,
          borderColor: "#E5E7EB",
          borderRadius: 12,
        }}>
        <View style={{ display: "flex", flexDirection: "column" }}>
          <View style={styles.profilePictureSection}>
            <Controller
              control={control}
              name="profilePicture"
              render={({ field: { onChange, value } }) => (
                <ImageUpload
                  value={value}
                  onChange={onChange}
                  storagePath={StoragePathType.UsersProfileImages}
                  containerStyle={{
                    width: 160,
                    height: 160,
                    borderRadius: 80,
                    borderWidth: 1,
                    borderColor: "#E5E7EB",
                    backgroundColor: "#F3F4F6",
                    borderStyle: "solid",
                  }}
                  imageStyle={{
                    borderRadius: 80,
                    overflow: "hidden",
                  }}
                  aspectRatio={[1, 1]}
                  allowsEditing={true}
                  onError={(error) => setUploadError(error)}
                  placeholderContent={
                    <View style={styles.profilePicturePlaceholder}>
                      <CustomText
                        text={
                          meData?.me?.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("") || ""
                        }
                        size="l"
                        weight="ultraBold"
                        style={styles.placeholderText}
                        useTranslationText={false}
                      />
                    </View>
                  }
                />
              )}
            />
          </View>
          <CustomSpacing type="vertical" size="s" />
          <CustomText text="Profile Photo" size="m" style={styles.profilePictureText} />
          <CustomText text="Recommended size: 500x500" size="xs" style={styles.helperText} />
          {uploadError && <CustomText text={uploadError} size="xs" style={styles.errorText} />}
        </View>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Full Name" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="name"
              disabled={true}
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="Enter your full name"
                  style={{}}
                  error={errors.name?.message}
                  disabled={true}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Email Address" size="s" weight="ultraBold" style={{}} />
            <CustomText
              text="This is the email address students can use to contact you directly."
              size="xs"
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="email"
              disabled={true}
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="Enter your email address"
                  style={{}}
                  error={errors.email?.message}
                  disabled={true}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Calendly Scheduling Link" size="s" weight="ultraBold" style={{}} />
            <CustomText
              text="This link allows students to schedule time with you for one-on-one sessions."
              size="xs"
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="meetingLink"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="e.g., https://calendly.com/yourname"
                  style={{}}
                  error={errors.meetingLink?.message}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Bio" size="s" weight="ultraBold" style={{}} />
            <CustomText text="Tell your students a little bit about yourself" size="xs" style={styles.helperText} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="biography"
              render={({ field: { value, onChange } }) => {
                const handleOnChange = (content: string) => {
                  setActivelyEditing(true);
                  onChange(content);
                  setValue("biography", content, { shouldDirty: true, shouldValidate: false });
                };

                return (
                  <CustomRichTextEditor
                    value={value}
                    onChange={handleOnChange}
                    isSubmitting={false}
                    placeholder="Tell your students a little bit about yourself"
                    activelyEditing={activelyEditing}
                    restrictImageUpload={true}
                  />
                );
              }}
            />

            {errors.biography?.message && (
              <CustomText text={errors.biography.message} size="xs" style={styles.errorText} />
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    color: "#111827",
  },
  form: {
    flex: 1,
  },
  formGroup: {
    width: "100%",
  },
  profilePictureText: {
    color: "#111827",
    fontSize: 16,
    fontWeight: "bold",
  },
  helperText: {
    color: "#6B7280",
    marginTop: 4,
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  profilePictureSection: {
    alignItems: "center",
    position: "relative",
  },
  profilePicturePlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
  },
  placeholderText: {
    color: "#6B7280",
    fontSize: 40,
  },
});

export default FacultyProfileTabContainer;
